/**
 * Fixes a real bug (not a stock/availability issue) on products with a
 * highly irregular option matrix - e.g. mesh-dump-truck-tarps-united-
 * tarps, where "Tarp Width" values each pair with only a handful of
 * specific "Tarp Length" values, not the full cross-product of every
 * width x every length.
 *
 * Reproduced and confirmed the root cause directly: picking a new Width
 * that doesn't happen to pair with whatever Length was PREVIOUSLY
 * selected (e.g. switching from the default "4'6\" / 10'" to "5'", which
 * only pairs with 12'/14', never 10') leaves the Length <select> holding
 * a now-invalid, stale value. Dawn's own variant-picker JS
 * (product-info.js) sends the CURRENT value of every option select to
 * Shopify to resolve the matching variant - including that stale one -
 * and neither Dawn's client code nor Shopify's option_values endpoint
 * corrects it: a curl replica of that exact request returns a bare
 * `null` for "selected_or_first_available_variant", which is what
 * actually produces the "Unavailable" button state. It isn't that the
 * combination is out of stock - it's that the combination requested
 * was never a real variant to begin with.
 *
 * First attempt (kept here as history, in the git diff, not in code):
 * detect the mismatch and correct the Length <select>'s value directly
 * before the event reaches Dawn's own handler. That failed for a
 * different reason specific to this theme: sections/main-product.liquid
 * (via snippets/product-variant-options.liquid) OMITS unavailable
 * option values from the DOM entirely instead of just disabling them
 * (a deliberate, separate customization - see that file's own comment).
 * So at initial load, with Width="4'6\"" selected, the Length <select>
 * ONLY contains <option value="10'"> - "12'"/"14'" don't exist in the
 * DOM to select into at all, confirmed live via instrumented tracing.
 *
 * This version sidesteps that entirely: rather than trying to keep two
 * dependent <select>s in sync client-side, it detects the mismatch,
 * works out which real variant the user almost certainly meant (the one
 * that keeps whatever they just changed fixed, preferring an available
 * one), and re-drives Dawn's OWN existing update pipeline
 * (product-info.js's renderProductInfo/handleUpdateProductInfo) with a
 * corrected `?variant=<id>` request instead of the broken
 * `?option_values=...` one Dawn would have built. `?variant=` resolves
 * unambiguously regardless of what's currently sitting in any <select>,
 * and reusing Dawn's own pipeline (rather than reimplementing price/
 * media/quantity-rule/ATC-button updates here) means every one of those
 * still updates correctly, including the corrected HTML replacing the
 * <variant-selects> DOM so the Length <select> ends up with the right
 * options going forward too.
 */
(function () {
  function getVariantsData(variantSelects) {
    try {
      return JSON.parse(variantSelects.querySelector('[data-all-variants]')?.textContent);
    } catch {
      return null;
    }
  }

  function getOptionControls(variantSelects) {
    // One control per option position, in the same order product.options_with_values
    // renders them in - matches how Dawn's own getAllSelectedOptions() groups things.
    return Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown')).map((group) => ({
      group,
      select: group.querySelector('select'),
    }));
  }

  function getControlValue({ group, select }) {
    if (select) return select.value;
    const checked = group.querySelector('input:checked');
    return checked ? checked.value : null;
  }

  function fixStaleSelection(event) {
    const variantSelects = event.currentTarget.closest('variant-selects');
    if (!variantSelects) return;

    const variants = getVariantsData(variantSelects);
    if (!variants || !variants.length) return;

    const controls = getOptionControls(variantSelects);
    const currentValues = controls.map(getControlValue);

    const matchesExactly = (variant) =>
      currentValues.every((value, index) => value == null || variant[`option${index + 1}`] === value);

    if (variants.some(matchesExactly)) return; // a real, existing combination - let Dawn handle it normally.

    const changedIndex = controls.findIndex(({ group }) => group.contains(event.target));
    if (changedIndex === -1) return;

    const changedValue = currentValues[changedIndex];
    const candidates = variants.filter((variant) => variant[`option${changedIndex + 1}`] === changedValue);
    if (!candidates.length) return; // the just-picked value itself doesn't exist on any variant - nothing we can do.

    const replacement = candidates.find((variant) => variant.available) || candidates[0];

    const productInfo = variantSelects.closest('product-info');
    if (!productInfo || typeof productInfo.renderProductInfo !== 'function') return;

    // Stop Dawn's own delegated listener (on <variant-selects>, bubble
    // phase - this listener runs first since it's bound on the <select>
    // itself) from ALSO firing for this event with the stale/invalid
    // option_values combination. We're taking over just this one
    // lookup, not replacing Dawn's update pipeline - handleUpdateProductInfo
    // below is the exact same method Dawn's own handler would have called.
    event.stopImmediatePropagation();

    const productUrl = event.target.dataset.productUrl || productInfo.dataset.url;
    const requestUrl = `${productUrl}?section_id=${productInfo.sectionId}&variant=${replacement.id}`;

    productInfo.renderProductInfo({
      requestUrl,
      targetId: event.target.id,
      callback: productInfo.handleUpdateProductInfo(productUrl),
    });
  }

  function bind(variantSelects) {
    if (variantSelects.dataset.staleOptionFixBound) return;
    variantSelects.dataset.staleOptionFixBound = 'true';
    variantSelects.querySelectorAll('select, fieldset input').forEach((control) => {
      control.addEventListener('change', fixStaleSelection);
    });
  }

  document.querySelectorAll('variant-selects').forEach(bind);

  // Same reasoning as judgeme-tab-dismiss.js/konfig-links-new-tab.js
  // elsewhere in this theme: product-info's own HTMLUpdateUtility can
  // replace/re-render a <variant-selects> element after a variant
  // change (view transitions), so keep checking for new instances
  // rather than assuming this runs once and is done.
  setInterval(function () {
    document.querySelectorAll('variant-selects').forEach(bind);
  }, 500);
})();
